import Axios, { AxiosPromise } from 'axios';
import { EPhoneNumberVerificationType, ERestAPIBaseUrl } from '../util/constants';
import {
    TCsrfResponse,
    TLoginResponse,
    TRegister3rdPartyResponse,
    TRegister3rdPartyPost,
    TLoginPost,
    TForgotPasswordPost,
    TForgotPasswordResponse,
    TResetPasswordPost,
    TResetPasswordResponse,
    TCountriesListResponse,
    TVerifyAccountResponse,
    TLoginInfoResponse,
    TSiteInfoResponse,
    TChangeEmailResponse,
    TCreateAccountPost,
    TCreateAccountResponse,
    TRequestVerifyPost,
    TRequestVerifyResponse,
    TSetupRegister3rdPartyResponse,
    TStartGraduationResponse,
    TCompleteGraduationResponse,
    TCompleteGraduationPost,
    TAgreeToTermsPost,
    TStartConfirmAccountResponse,
    TCompleteConfirmAccountResponse,
    TAgreeToTermsResponse,
    TTrackEventPost,
    TForgotPasswordStaffPost,
    TResetPasswordStaffPost,
    TChangeEmailStaffResponse,
    TRequestVerifyStaffPost,
    TStartConfirmStaffAccountResponse,
    TCompleteConfirmStaffAccountPost,
    TStartConfirmStaffPhoneNumberPost,
    TCompleteConfirmStaffPhoneNumberPost,
    TSetupConsentResponse,
    TConsentPost,
    TConsentResponse,
    TMigrateStaffAccountPost,
    TMigrateStaffAccountResponse,
    TMfaVerifyPost,
    TMfaRecoveryPost,
    TLogoutPost,
    TLogoutResponse,
    TFindConsumerPost,
    TFindConsumerResponse,
    TAddPasswordPost,
    TVerifyAndCreateDependentParams,
    TVerifyAndCreateDependentResponse,
    TRequestVerifyLoginPost,
    TVerifyForgotPasswordPost,
    TVerifyForgotPasswordResponse,
    TRequestConfirmStaffAccountPost,
} from './types';

export enum EGatewayAPIBaseUrl {
    local = 'http://localhost:8080',
    staging = 'https://www.staging.arcusplatform.io/identity/gateway',
    devSandbox = 'https://www.staging.arcusplatform.io/identity-sandbox/gateway',
    production = 'https://www.mindbodyapis.com/identity/gateway',
}

export type TApi = {
    get: {
        csrfToken: () => AxiosPromise<TCsrfResponse>;
        countriesList: (APIBaseUrl: EGatewayAPIBaseUrl) => AxiosPromise<TCountriesListResponse>;
        setupRegister3rdParty: () => AxiosPromise<TSetupRegister3rdPartyResponse>;
        verifyAccount: (queryParams: string) => AxiosPromise<TVerifyAccountResponse>;
        verifyAndCreateDependent: (
            queryParams: TVerifyAndCreateDependentParams,
        ) => AxiosPromise<TVerifyAndCreateDependentResponse>;
        verifyStaffAccount: (queryParams: string) => AxiosPromise<null>;
        siteInfo: (
            APIBaseURL: ERestAPIBaseUrl,
            queryParams: string,
            subscriberId: string,
        ) => AxiosPromise<TSiteInfoResponse>;
        loginInfo: (queryParams: string) => AxiosPromise<TLoginInfoResponse>;
        changeEmail: (queryParams: string) => AxiosPromise<TChangeEmailResponse>;
        changeStaffEmail: (queryParams: string) => AxiosPromise<TChangeEmailStaffResponse>;
        startGraduation: (queryParams: string) => AxiosPromise<TStartGraduationResponse>;
        startConfirmAccount: (queryParams: string) => AxiosPromise<TStartConfirmAccountResponse>;
        startConfirmStaffAccount: (queryParams: string) => AxiosPromise<TStartConfirmStaffAccountResponse>;
        setupConsent: (queryParams: string) => AxiosPromise<TSetupConsentResponse>;
    };
    post: {
        trackEvent: (payload: TTrackEventPost) => void;
        login: (payload: TLoginPost, queryParams: string, csrfToken: string) => AxiosPromise<TLoginResponse>;
        logout: (paylod: TLogoutPost, csrfToken: string) => AxiosPromise<TLogoutResponse>;
        multiFactorVerify: (payload: TMfaVerifyPost, csrfToken: string) => AxiosPromise<null>;
        multiFactorRecovery: (payload: TMfaRecoveryPost, csrfToken: string) => AxiosPromise<null>;
        forgotPassword: (
            APIBaseURL: EGatewayAPIBaseUrl,
            payload: TForgotPasswordPost,
        ) => AxiosPromise<TForgotPasswordResponse>;
        forgotPasswordStaff: (APIBaseURL: EGatewayAPIBaseUrl, payload: TForgotPasswordStaffPost) => AxiosPromise<null>;
        verifyForgotPassword: (payload: TVerifyForgotPasswordPost) => AxiosPromise<TVerifyForgotPasswordResponse>;
        resetPassword: (
            APIBaseURL: EGatewayAPIBaseUrl,
            payload: TResetPasswordPost,
            userID: string,
        ) => AxiosPromise<TResetPasswordResponse>;
        resetPasswordStaff: (
            APIBaseUrl: EGatewayAPIBaseUrl,
            payload: TResetPasswordStaffPost,
            userId: string,
        ) => AxiosPromise<{}>;
        register3rdParty: (
            payload: TRegister3rdPartyPost,
            queryParams: string,
            csrfToken: string,
        ) => AxiosPromise<TRegister3rdPartyResponse>;
        requestVerify: (
            APIBaseURL: EGatewayAPIBaseUrl,
            payload: TRequestVerifyPost,
        ) => AxiosPromise<TRequestVerifyResponse>;
        requestVerifyLogin: (APIBaseUrl: EGatewayAPIBaseUrl, payload: TRequestVerifyLoginPost) => AxiosPromise<null>;
        requestVerifyStaff: (APIBaseURL: EGatewayAPIBaseUrl, payload: TRequestVerifyStaffPost) => AxiosPromise<null>;
        createAccount: (
            APIBaseUrl: EGatewayAPIBaseUrl,
            payload: TCreateAccountPost,
        ) => AxiosPromise<TCreateAccountResponse>;
        completeGraduation: (
            userId: string,
            payload: TCompleteGraduationPost,
        ) => AxiosPromise<TCompleteGraduationResponse>;
        completeConfirmAccount: (
            userId: string,
            payload: TCompleteGraduationPost,
        ) => AxiosPromise<TCompleteConfirmAccountResponse>;
        completeConfirmStaffAccount: (userId: string, payload: TCompleteConfirmStaffAccountPost) => AxiosPromise<null>;
        requestConfirmStaffAccount: (
            APIBaseURL: EGatewayAPIBaseUrl,
            userId: string,
            payload: TRequestConfirmStaffAccountPost,
        ) => AxiosPromise<null>;
        startConfirmStaffPhoneNumber: (
            payload: TStartConfirmStaffPhoneNumberPost,
            csrfToken: string,
        ) => AxiosPromise<{}>;
        completeConfirmStaffPhoneNumber: (
            payload: TCompleteConfirmStaffPhoneNumberPost,
            csrfToken: string,
        ) => AxiosPromise<{}>;
        migrateStaffAccount: (
            APIBaseURL: EGatewayAPIBaseUrl,
            payload: TMigrateStaffAccountPost,
        ) => AxiosPromise<TMigrateStaffAccountResponse>;
        agreeToTerms: (payload: TAgreeToTermsPost, csrfToken: string) => AxiosPromise<TAgreeToTermsResponse>;
        consent: (payload: TConsentPost, csrfToken: string) => AxiosPromise<TConsentResponse>;
        findConsumer: (payload: TFindConsumerPost, csrfToken: string) => AxiosPromise<TFindConsumerResponse>;
        addPassword: (payload: TAddPasswordPost, userId: string) => AxiosPromise<null>;
    };
    delete: {
        multiFactorDisable: (userId: string, code: string) => AxiosPromise<null>;
    };
};

const API: TApi = {
    get: {
        csrfToken: () => {
            const response = Axios.get('/api/csrf');
            return response;
        },
        countriesList: (APIBaseURL) => {
            const response = Axios.get(`${APIBaseURL}/v2/countries`);
            return response;
        },
        setupRegister3rdParty: () => {
            const response = Axios.get('/external/register');
            return response;
        },
        verifyAccount: (queryParams) => {
            const response = Axios.get(`/account/verify${queryParams}`);
            return response;
        },
        verifyAndCreateDependent: (queryParams) => {
            const { firstName, lastName, userId, profileId, code, subscriberId } = queryParams;
            const parsedQueryParams = `?firstName=${firstName}&lastName=${lastName}&userId=${userId}&profileId=${profileId}&code=${code}&subscriberId=${subscriberId}`;
            const response = Axios.get(`/account/verify/loginandcreatedependent${parsedQueryParams}`);
            return response;
        },
        verifyStaffAccount: (queryParams) => {
            const response = Axios.get(`/staff/verify${queryParams}`);
            return response;
        },
        siteInfo: (APIBaseURL, queryParams, subscriberId) => {
            const response = Axios.get(`${APIBaseURL}/subscribers/siteInformation${queryParams}`, {
                headers: { siteId: subscriberId },
            });
            return response;
        },
        loginInfo: (queryParams) => {
            const response = Axios.get(`/account/loginInfo${queryParams}`);
            return response;
        },
        changeEmail: (queryParams) => {
            const response = Axios.get(`/account/changeEmail${queryParams}`);
            return response;
        },
        changeStaffEmail: (queryParams) => {
            const response = Axios.get(`/staff/changeEmail${queryParams}`);
            return response;
        },
        startGraduation: (queryParams) => {
            const response = Axios.get(`/account/graduations${queryParams}`);
            return response;
        },
        startConfirmAccount: (queryParams) => {
            const response = Axios.get(`/account/confirmations${queryParams}`);
            return response;
        },
        startConfirmStaffAccount: (queryParams) => {
            const response = Axios.get(`/staff/onboardings${queryParams}`);
            return response;
        },
        setupConsent: (queryParams) => {
            const response = Axios.get(`/consents${queryParams}`);
            return response;
        },
    },
    post: {
        trackEvent: (payload) => {
            Axios.post(`/metrics/events`, payload);
        },
        login: (payload, queryParams, csrfToken) => {
            const response = Axios.post(`/account/login${queryParams}`, payload, {
                headers: { requestVerificationToken: csrfToken },
            });
            return response;
        },
        logout: (payload, csrfToken) => {
            const response = Axios.post('/account/logouts', payload, {
                headers: { requestVerificationToken: csrfToken },
            });
            return response;
        },
        multiFactorVerify: (payload, csrfToken) => {
            const response = Axios.post('/v1/mfa/login', payload, { headers: { requestVerificationToken: csrfToken } });
            return response;
        },
        multiFactorRecovery(payload, csrfToken) {
            const response = Axios.post('/v1/mfa/recoverycodelogin', payload, {
                headers: { requestVerificationToken: csrfToken },
            });
            return response;
        },
        forgotPassword: (APIBaseURL, payload) => {
            const response = Axios.post(`${APIBaseURL}/v2/users/resetPasswordRequests`, payload);
            return response;
        },
        forgotPasswordStaff: (APIBaseUrl, payload) => {
            const response = Axios.post(`${APIBaseUrl}/v2/staff/resetPasswordRequests`, payload);
            return response;
        },
        verifyForgotPassword: (payload) => {
            const response = Axios.post(`/account/exchangePasswordResetPasscode`, payload);
            return response;
        },
        resetPassword: (APIBaseURL, payload, userID) => {
            const response = Axios.post(`${APIBaseURL}/v2/users/${userID}/passwords`, payload);
            return response;
        },
        resetPasswordStaff: (APIBaseUrl, payload, userId) => {
            const response = Axios.post(`${APIBaseUrl}/v2/staff/${userId}/passwords`, payload);
            return response;
        },
        register3rdParty: (payload, queryParams, csrfToken) => {
            const response = Axios.post(`/external/completeRegistration${queryParams}`, payload, {
                headers: { requestVerificationToken: csrfToken },
            });
            return response;
        },
        requestVerify: (APIBaseUrl, payload) => {
            const response = Axios.post(`${APIBaseUrl}/v2/users/verificationRequests`, payload);
            return response;
        },
        requestVerifyLogin: (ERestAPIBaseUrl, payload) => {
            const response = Axios.post(`${ERestAPIBaseUrl}/v2/users/verificationRequests/login`, payload);
            return response;
        },
        requestVerifyStaff: (APIBaseUrl, payload) => {
            const response = Axios.post(`${APIBaseUrl}/v2/staff/verificationRequests`, payload);
            return response;
        },
        createAccount: (APIBaseURL, payload) => {
            const response = Axios.post(`${APIBaseURL}/v2/users`, payload);
            return response;
        },
        completeGraduation: (userId, payload) => {
            const response = Axios.post(`/account/graduations/${userId}`, payload);
            return response;
        },
        completeConfirmAccount: (userId, payload) => {
            const response = Axios.post(`/account/confirmations/${userId}`, payload);
            return response;
        },
        completeConfirmStaffAccount: (userId, payload) => {
            const response = Axios.post(`/staff/onboardings/${userId}`, payload);
            return response;
        },
        requestConfirmStaffAccount: (APIBaseURL, userId, payload) => {
            const response = Axios.post(`${APIBaseURL}/v2/staff/${userId}/onboardingRequests`, payload);
            return response;
        },
        startConfirmStaffPhoneNumber: (payload, csrfToken) => {
            const response = Axios.post(`/staff/phones/verificationRequests`, payload, {
                headers: { requestVerificationToken: csrfToken },
            });
            return response;
        },
        completeConfirmStaffPhoneNumber: (payload, csrfToken) => {
            const response = Axios.post('/staff/phones', payload, {
                headers: { requestVerificationToken: csrfToken },
            });
            return response;
        },
        migrateStaffAccount: (APIBaseUrl, payload) => {
            const response = Axios.post(`${APIBaseUrl}/v2/staff/migrations`, payload);
            return response;
        },
        agreeToTerms: (payload, csrfToken) => {
            const response = Axios.post(`/account/agreements`, payload, {
                headers: { requestVerificationToken: csrfToken },
            });
            return response;
        },
        consent: (payload, csrfToken) => {
            const response = Axios.post('/consents', payload, {
                headers: { requestVerificationToken: csrfToken },
            });
            return response;
        },
        findConsumer: (payload, csrfToken) => {
            const response = Axios.post('/account/search', payload, {
                headers: { requestVerificationToken: csrfToken },
            });
            return response;
        },
        addPassword: (payload, userId) => {
            const response = Axios.post(`/account/setpassword/${userId}`, payload);
            return response;
        },
    },
    delete: {
        multiFactorDisable: (userId, code) => {
            const response = Axios.delete(`/v1/mfa?userId=${userId}&code=${code}`);
            return response;
        },
    },
};

export default API;
